import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  LineChart, Line
} from 'recharts';
import { getHistory, loadHistory as loadHistoryFromServer } from './storage';
import { useTheme } from './ThemeContext';
import { Icons } from './icons';

function Stats({ dailyNorms, onBack }) {
  const { colors } = useTheme();
  const [weekData, setWeekData] = useState([]);
  const [weightHistory, setWeightHistory] = useState([]);
  const [avgMacros, setAvgMacros] = useState({ protein: 0, fat: 0, carbs: 0 });
  const [totalMeals, setTotalMeals] = useState(0);
  const [bestDay, setBestDay] = useState(null);

  useEffect(() => {
    const init = async () => {
      await loadHistoryFromServer();
      calculateStats();
      loadWeightHistory();
    };
    init();
  }, []);

  const loadWeightHistory = () => {
    const saved = localStorage.getItem('userProfile');
    if (saved) {
      try {
        const profile = JSON.parse(saved);
        if (profile.weightHistory && profile.weightHistory.length > 0) {
          const sorted = [...profile.weightHistory].sort((a, b) => a.date.localeCompare(b.date));
          setWeightHistory(sorted);
        }
      } catch (e) {}
    }
  };

  const calculateStats = () => {
    const history = getHistory();
    if (history.length === 0) return;

    setTotalMeals(history.length);

    const last7Days = {};
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toLocaleDateString('ru-RU');
      last7Days[dateStr] = { date: dateStr, calories: 0, protein: 0, fat: 0, carbs: 0, count: 0 };
    }

    let totalProtein = 0, totalFat = 0, totalCarbs = 0, mealsCount = 0;
    let maxCalories = 0;
    let bestDayDate = null;

    history.forEach(meal => {
      const data = meal.mealText;
      const calMatch = data.match(/Калории:\s*([\d.]+)\s*ккал/);
      const protMatch = data.match(/Белки:\s*([\d.]+)\s*г/);
      const fatMatch = data.match(/Жиры:\s*([\d.]+)\s*г/);
      const carbMatch = data.match(/Углеводы:\s*([\d.]+)\s*г/);

      const calories = calMatch ? parseInt(calMatch[1].replace(/\s/g, '')) : 0;
      const protein = protMatch ? parseFloat(protMatch[1]) : 0;
      const fat = fatMatch ? parseFloat(fatMatch[1]) : 0;
      const carbs = carbMatch ? parseFloat(carbMatch[1]) : 0;

      if (calories > 0) {
        totalProtein += protein;
        totalFat += fat;
        totalCarbs += carbs;
        mealsCount++;

        if (last7Days[meal.date]) {
          last7Days[meal.date].calories += calories;
          last7Days[meal.date].protein += protein;
          last7Days[meal.date].fat += fat;
          last7Days[meal.date].carbs += carbs;
          last7Days[meal.date].count++;
        }
      }
    });

    if (mealsCount > 0) {
      setAvgMacros({
        protein: Math.round(totalProtein / mealsCount),
        fat: Math.round(totalFat / mealsCount),
        carbs: Math.round(totalCarbs / mealsCount)
      });
    }

    const weekArray = Object.values(last7Days);
    weekArray.forEach(day => {
      if (day.calories > maxCalories) {
        maxCalories = day.calories;
        bestDayDate = day.date;
      }
    });

    setWeekData(weekArray.map(day => ({
      ...day,
      displayDate: day.date.slice(0, 5)
    })));

    if (bestDayDate) {
      setBestDay({ date: bestDayDate, calories: maxCalories });
    }
  };

  const pieData = [
    { name: 'Белки', value: avgMacros.protein * 4, color: '#5DCAA5' },
    { name: 'Жиры', value: avgMacros.fat * 9, color: '#FAC775' },
    { name: 'Углеводы', value: avgMacros.carbs * 4, color: '#85B7EB' }
  ].filter(item => item.value > 0);

  const COLORS = ['#5DCAA5', '#FAC775', '#85B7EB'];

  const s = {
    page: { maxWidth: '420px', margin: '0 auto', minHeight: '100vh', background: colors.page, fontFamily: 'system-ui, -apple-system, sans-serif' },
    header: { background: colors.header, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    backBtn: { background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px' },
    headerTitle: { fontSize: '18px', fontWeight: '600', color: '#fff' },
    headerPlaceholder: { width: '40px' },
    content: { padding: '16px' },
    statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' },
    statCard: { background: colors.card, borderRadius: '16px', padding: '14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
    statValue: { fontSize: '24px', fontWeight: '700', color: colors.text },
    statLabel: { fontSize: '12px', color: colors.textMuted, marginTop: '4px' },
    chartCard: { background: colors.card, borderRadius: '20px', padding: '16px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
    chartTitle: { fontSize: '14px', fontWeight: '600', color: colors.textSecondary, marginBottom: '12px' },
    normLine: { textAlign: 'center', fontSize: '13px', color: colors.textMuted, marginTop: '8px' },
    macrosCard: { background: colors.card, borderRadius: '20px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
    macrosTitle: { fontSize: '14px', fontWeight: '600', color: colors.textSecondary, marginBottom: '16px' },
    macrosRow: { display: 'flex', flexDirection: 'column', gap: '10px' },
    macroItem: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: colors.textLight },
    macroDot: { width: '10px', height: '10px', borderRadius: '50%' },
    emptyState: { padding: '60px 20px', textAlign: 'center' },
    emptyIcon: { fontSize: '64px', marginBottom: '20px' },
    emptyText: { fontSize: '18px', fontWeight: '600', color: colors.textSecondary, marginBottom: '8px' },
    emptySubtext: { fontSize: '14px', color: colors.textMuted }
  };

  if (weekData.length === 0) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={onBack}><Icons.back size={24} color="#fff" /></button>
          <div style={s.headerTitle}><Icons.stats size={18} color="#fff" /> Статистика</div>
          <div style={s.headerPlaceholder}></div>
        </div>
        <div style={s.emptyState}>
          <div style={s.emptyIcon}><Icons.stats size={64} color={colors.textMuted} /></div>
          <div style={s.emptyText}>Нет данных для статистики</div>
          <div style={s.emptySubtext}>Добавьте приёмы пищи через главный экран</div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}><Icons.back size={24} color="#fff" /></button>
        <div style={s.headerTitle}><Icons.stats size={18} color="#fff" /> Статистика</div>
        <div style={s.headerPlaceholder}></div>
      </div>

      <div style={s.content}>
        <div style={s.statsGrid}>
          <div style={s.statCard}><div style={s.statValue}>{totalMeals}</div><div style={s.statLabel}>приёмов</div></div>
          <div style={s.statCard}><div style={s.statValue}>{weekData.filter(d => d.calories > 0).length}</div><div style={s.statLabel}>дней с записями</div></div>
          {bestDay && (<div style={{...s.statCard, gridColumn: 'span 2'}}><div style={s.statValue}>{bestDay.calories} ккал</div><div style={s.statLabel}>лучший день: {bestDay.date}</div></div>)}
        </div>

        <div style={s.chartCard}>
          <div style={s.chartTitle}>Калории за 7 дней</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weekData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis dataKey="displayDate" tick={{ fontSize: 11, fill: colors.textMuted }} />
              <YAxis tick={{ fontSize: 11, fill: colors.textMuted }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(value) => [`${value} ккал`, 'Калории']} />
              <Bar dataKey="calories" fill="#1D9E75" radius={[8, 8, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
          <div style={s.normLine}>Норма: <strong>{dailyNorms.calories} ккал/день</strong></div>
        </div>

        <div style={s.chartCard}>
          <div style={s.chartTitle}>Среднее распределение калорий</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: colors.textMuted, strokeWidth: 1 }}>
                {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: 'none' }} formatter={(value) => [`${value} ккал`]} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* График веса */}
        {weightHistory.length >= 2 && (
          <div style={s.chartCard}>
            <div style={s.chartTitle}><Icons.weight size={16} color={colors.textSecondary} /> Динамика веса</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={weightHistory.map(e => ({ ...e, displayDate: new Date(e.date).toLocaleDateString('ru-RU', {day:'numeric',month:'short'}) }))} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis dataKey="displayDate" tick={{ fontSize: 11, fill: colors.textMuted }} />
                <YAxis tick={{ fontSize: 11, fill: colors.textMuted }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none' }} formatter={(value) => [`${value} кг`, 'Вес']} />
                <Line type="monotone" dataKey="weight" stroke="#1D9E75" strokeWidth={2} dot={{ r: 4, fill: '#1D9E75' }} />
              </LineChart>
            </ResponsiveContainer>
            {weightHistory.length >= 2 && (
              <div style={s.normLine}>
                {(() => {
                  const first = weightHistory[0].weight;
                  const last = weightHistory[weightHistory.length - 1].weight;
                  const diff = last - first;
                  return `Изменение: ${diff > 0 ? '+' : ''}${diff.toFixed(1)} кг`;
                })()}
              </div>
            )}
          </div>
        )}

        <div style={s.macrosCard}>
          <div style={s.macrosTitle}>Средние макросы за приём</div>
          <div style={s.macrosRow}>
            <div style={s.macroItem}><div style={{...s.macroDot, background: '#5DCAA5'}}></div><span>Белки: <strong>{avgMacros.protein} г</strong></span></div>
            <div style={s.macroItem}><div style={{...s.macroDot, background: '#FAC775'}}></div><span>Жиры: <strong>{avgMacros.fat} г</strong></span></div>
            <div style={s.macroItem}><div style={{...s.macroDot, background: '#85B7EB'}}></div><span>Углеводы: <strong>{avgMacros.carbs} г</strong></span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Stats;